import { useEffect, useSyncExternalStore } from "react";
import { mathMacrosVersion, setMathMacros, subscribeMathMacros } from "@/lib/katex-config";

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * The maths macros this document renders with (18 Oct): asked of the server, which writes the same
 * shortcuts into the PDF and adds the document's own preamble macros, so a \R the author defined
 * shows in the editor as it prints. Asked again when the custom preamble changes; Lilia's shortcuts
 * alone until the answer comes, and again once the document is closed.
 */
export function useDocumentMathMacros(documentId: string | undefined, customPreamble: string | null | undefined, fetchWithAuth: Fetch): void {
  useEffect(() => {
    if (!documentId) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/documents/${documentId}/preview/math-macros`, { signal: ctrl.signal });
        if (!res.ok) return;
        const body = (await res.json()) as { macros?: Record<string, string> };
        if (!ctrl.signal.aborted && body.macros) setMathMacros(body.macros);
      } catch {
        // Offline or refused: Lilia's shortcuts stay.
      }
    })();
    return () => ctrl.abort();
  }, [documentId, customPreamble, fetchWithAuth]);

  useEffect(() => () => setMathMacros(null), [documentId]);
}

/** Changes whenever the macros do: a dependency for maths drawn in effects. */
export function useMathMacrosVersion(): number {
  return useSyncExternalStore(subscribeMathMacros, mathMacrosVersion, mathMacrosVersion);
}
